import { useCallback, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import { auth, db, storage } from "../firebase";
import { deleteAccount, requestDeleteCode } from "../services/api";
import "../styles/profile.css";

const editableFields = [
  "fullName",
  "phone",
  "institute",
  "roll",
  "programme",
  "department1",
  "department2",
  "interest",
];

const getInitials = (name, email) => {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }
  return (email || "U").slice(0, 2).toUpperCase();
};

const Profile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sendingDeleteCode, setSendingDeleteCode] = useState(false);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [stats, setStats] = useState({
    projects: 0,
    applications: 0,
  });
  const [statusMessage, setStatusMessage] = useState("");
  const [error, setError] = useState("");
  const [deleteCode, setDeleteCode] = useState("");
  const [deleteCodeSent, setDeleteCodeSent] = useState(false);

  // Resume state
  const [resumeName, setResumeName] = useState("");
  const [resumeFile, setResumeFile] = useState(null);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [resumeError, setResumeError] = useState("");
  const [resumeSuccess, setResumeSuccess] = useState("");
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [resumeToDelete, setResumeToDelete] = useState(null); // resume pending deletion confirm

  const isProfessor = profile?.role === "professor";
  const isStudent = profile?.role === "student";

  const visibleFields = useMemo(() => {
    if (isProfessor) {
      return [
        "fullName",
        "phone",
        "institute",
        "department1",
        "department2",
        "interest",
      ];
    }

    if (isStudent) {
      return [
        "fullName",
        "phone",
        "roll",
        "programme",
        "department1",
        "department2",
        "interest",
      ];
    }

    return editableFields;
  }, [isProfessor, isStudent]);

  const loadProfile = useCallback(async (currentUser) => {
    const userSnap = await getDoc(doc(db, "users", currentUser.uid));
    const userData = userSnap.data();

    const role = userData?.role;
    setProfile({ id: currentUser.uid, ...userData });
    setForm({
      fullName: userData?.fullName || "",
      phone: userData?.phone || "",
      institute: userData?.institute || "",
      roll: userData?.roll || "",
      programme: userData?.programme || "",
      department1: userData?.department1 || "",
      department2: userData?.department2 || "",
      interest: userData?.interest || "",
    });

    // Fetch stats in a separate try/catch so a permission error on stats
    // never surfaces as a "Failed to update profile" error.
    try {
      let projectCount = 0;
      let applicationCount = 0;

      if (role === "professor") {
        const projectSnapshot = await getDocs(
          query(collection(db, "projects"), where("createdBy", "==", currentUser.uid))
        );
        projectCount = projectSnapshot.size;

        const profAppsSnapshot = await getDocs(
          query(collection(db, "applications"), where("professorId", "==", currentUser.uid))
        );
        applicationCount = profAppsSnapshot.size;
      } else if (role === "student") {
        const studentAppsSnapshot = await getDocs(
          query(collection(db, "applications"), where("studentId", "==", currentUser.uid))
        );
        applicationCount = studentAppsSnapshot.size;
      }

      setStats({ projects: projectCount, applications: applicationCount });
    } catch (statsError) {
      // Stats failing should never block profile display or save.
      console.warn("Could not load profile stats:", statsError.message);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate("/login");
        return;
      }

      try {
        setLoading(true);
        setUser(currentUser);
        await loadProfile(currentUser);
      } catch (loadError) {
        setError(loadError.message);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [loadProfile, navigate]);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!user) return;

    setSaving(true);
    setError("");
    setStatusMessage("");

    try {
      const payload = {};
      visibleFields.forEach((field) => {
        payload[field] = form[field]?.trim() || null;
      });

      await updateDoc(doc(db, "users", user.uid), payload);
      await loadProfile(user);
      setStatusMessage("Profile updated successfully.");
    } catch (saveError) {
      setError("Failed to update profile: " + saveError.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSendDeleteCode = async () => {
    if (!user) return;

    setSendingDeleteCode(true);
    setError("");
    setStatusMessage("");

    try {
      const token = await user.getIdToken(true);
      await requestDeleteCode(token);
      setDeleteCodeSent(true);
      setStatusMessage("Deletion code sent to your email.");
    } catch (codeError) {
      const message = codeError.response?.data?.error || codeError.message;
      setError("Failed to send deletion code: " + message);
    } finally {
      setSendingDeleteCode(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user || !deleteCode.trim()) return;

    setDeleting(true);
    setError("");
    setStatusMessage("");

    try {
      const token = await user.getIdToken(true);
      await deleteAccount(token, deleteCode.trim());
      await signOut(auth);
      navigate("/signup");
    } catch (deleteError) {
      const message = deleteError.response?.data?.error || deleteError.message;
      setError("Failed to delete account: " + message);
      setDeleting(false);
    }
  };

  const handleUploadResume = async (e) => {
    e.preventDefault();
    if (!user) return;
    if (!resumeName.trim()) {
      setResumeError("Please enter a resume name");
      return;
    }
    if (!resumeFile) {
      setResumeError("Please select a resume file");
      return;
    }

    setUploadingResume(true);
    setResumeError("");
    setResumeSuccess("");

    try {
      const timestamp = Date.now();
      const storagePath = `resumes/${user.uid}/profile/${timestamp}-${resumeFile.name}`;
      const fileRef = ref(storage, storagePath);
      await uploadBytes(fileRef, resumeFile);
      const downloadUrl = await getDownloadURL(fileRef);

      const userRef = doc(db, "users", user.uid);
      const currentResumes = profile?.resumes || [];
      const newResume = {
        name: resumeName.trim(),
        url: downloadUrl,
        storagePath: storagePath,
        uploadedAt: new Date().toISOString(),
        approvalStatus: "pending",
      };
      
      const updatedResumes = [...currentResumes, newResume];
      await updateDoc(userRef, { resumes: updatedResumes });
      
      setResumeName("");
      setResumeFile(null);
      setFileInputKey(Date.now());
      setResumeSuccess("Resume uploaded successfully.");
      await loadProfile(user);
    } catch (err) {
      console.error("Resume upload error: ", err);
      setResumeError("Failed to upload resume: " + err.message);
    } finally {
      setUploadingResume(false);
    }
  };

  const handleRemoveResume = (resume) => {
    // Open the advisory modal instead of window.confirm
    setResumeToDelete(resume);
  };

  const handleConfirmRemoveResume = async () => {
    if (!user || !resumeToDelete) return;

    setUploadingResume(true);
    setResumeError("");
    setResumeSuccess("");
    setResumeToDelete(null);

    try {
      if (resumeToDelete.storagePath) {
        const fileRef = ref(storage, resumeToDelete.storagePath);
        await deleteObject(fileRef);
      }

      const userRef = doc(db, "users", user.uid);
      const currentResumes = profile?.resumes || [];
      const updatedResumes = currentResumes.filter(
        (r) => r.storagePath !== resumeToDelete.storagePath
      );

      await updateDoc(userRef, { resumes: updatedResumes });
      setResumeSuccess("Resume removed successfully.");
      await loadProfile(user);
    } catch (err) {
      console.error("Resume remove error: ", err);
      setResumeError("Failed to remove resume: " + err.message);
    } finally {
      setUploadingResume(false);
    }
  };

  const renderField = (field) => {
    const labels = {
      fullName: "Full Name",
      phone: "Phone",
      institute: "Institute",
      roll: "Roll Number",
      programme: "Programme",
      department1: "Primary Department",
      department2: "Secondary Department",
      interest: "Interest Area",
    };

    return (
      <label key={field} className="profile-field">
        <span>{labels[field]}</span>
        <input
          value={form[field] || ""}
          onChange={(event) => handleChange(field, event.target.value)}
          disabled={saving || deleting}
        />
      </label>
    );
  };

  const avatarInitial = getInitials(profile?.fullName, profile?.email);

  return (
    <>
    <AppShell showSearch={false}>
      <div className="shell-page profile-page">
        {loading ? (
          <div className="home-shimmer">
            <div className="home-shimmer-block" style={{ height: 140 }} />
            <div className="home-shimmer-block" style={{ height: 320 }} />
          </div>
        ) : (
          <>
            <section className="profile-hero">
              <div className="profile-hero-avatar">{avatarInitial}</div>
              <div className="profile-hero-info">
                <p className="profile-kicker">{profile?.role || "User"} Account</p>
                <h1>{profile?.fullName || "Profile"}</h1>
                <p className="profile-hero-email">{profile?.email}</p>
                <div className="profile-hero-chips">
                  <span className="profile-chip profile-chip-role">{profile?.role || "user"}</span>
                  <span className="profile-chip">{stats.projects} Project{stats.projects !== 1 ? "s" : ""}</span>
                  <span className="profile-chip">{stats.applications} Application{stats.applications !== 1 ? "s" : ""}</span>
                  <span className={`profile-chip ${profile?.emailVerified ? "profile-chip-verified" : "profile-chip-unverified"}`}>
                    {profile?.emailVerified ? "✓ Verified" : "Unverified"}
                  </span>
                </div>
              </div>
            </section>

            <section className="profile-grid">
              <div className="profile-main-area">
                <form className="profile-card profile-form" onSubmit={handleSave}>
                  <div className="profile-section-header">
                    <div>
                      <h2>Profile Details</h2>
                      <p>Keep your FEP account information current.</p>
                    </div>
                    <button className="profile-primary-btn" disabled={saving || deleting}>
                      {saving ? "Saving..." : "Save Changes"}
                    </button>
                  </div>

                  {error && <div className="profile-error">{error}</div>}
                  {statusMessage && <div className="profile-success">{statusMessage}</div>}

                  <div className="profile-fields">{visibleFields.map(renderField)}</div>
                </form>

                {isStudent && (
                  <div className="profile-card profile-resumes-card">
                    <div className="profile-section-header">
                      <div>
                        <h2>Manage Resumes</h2>
                        <p>Upload your resumes to use them when applying for projects.</p>
                      </div>
                    </div>

                    {resumeError && <div className="profile-error">{resumeError}</div>}
                    {resumeSuccess && <div className="profile-success">{resumeSuccess}</div>}

                    <div className="uploaded-resumes-list">
                      <h3>Your Resumes</h3>
                      {!profile?.resumes || profile.resumes.length === 0 ? (
                        <p className="no-resumes-text">No resumes uploaded yet. Upload your first resume below.</p>
                      ) : (
                        <div className="resume-items">
                          {profile.resumes.map((resume, idx) => {
                            const status = resume.approvalStatus || "approved";
                            return (
                              <div key={idx} className="resume-item-row">
                                <div className="resume-item-info">
                                  <span className="resume-file-icon">📄</span>
                                  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                    <span className="resume-item-name">{resume.name}</span>
                                    <span className="resume-upload-date">
                                      Uploaded: {resume.uploadedAt ? new Date(resume.uploadedAt).toLocaleDateString() : "N/A"}
                                    </span>
                                  </div>
                                  <span className={`profile-badge badge-${status}`}>
                                    {status}
                                  </span>
                                </div>
                                <div className="resume-item-actions">
                                  <a
                                    href={resume.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="resume-action-view"
                                  >
                                    View
                                  </a>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveResume(resume)}
                                    className="resume-action-remove"
                                    disabled={uploadingResume}
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <form onSubmit={handleUploadResume} className="resume-upload-form">
                      <h3>Upload New Resume</h3>
                      <div className="resume-upload-fields">
                        <label className="profile-field">
                          <span>Resume Name</span>
                          <input
                            type="text"
                            value={resumeName}
                            onChange={(e) => setResumeName(e.target.value)}
                            placeholder="e.g. Software Engineering CV"
                            disabled={uploadingResume}
                            required
                          />
                        </label>
                        <label className="profile-field">
                          <span>Select PDF File</span>
                          <input
                            key={fileInputKey}
                            type="file"
                            accept=".pdf"
                            onChange={(e) => setResumeFile(e.target.files[0])}
                            disabled={uploadingResume}
                            required
                          />
                        </label>
                      </div>
                      <button
                        type="submit"
                        className="profile-primary-btn resume-upload-btn"
                        disabled={uploadingResume || !resumeName.trim() || !resumeFile}
                      >
                        {uploadingResume ? "Uploading..." : "Upload Resume"}
                      </button>
                    </form>
                  </div>
                )}
              </div>

              <aside className="profile-side">
                <div className="profile-card profile-quick-info">
                  <h2>Account Info</h2>
                  <div className="profile-info-rows">
                    <div className="profile-info-row">
                      <span>Email</span>
                      <strong>{profile?.email || "N/A"}</strong>
                    </div>
                    <div className="profile-info-row">
                      <span>Role</span>
                      <strong style={{ textTransform: "capitalize" }}>{profile?.role || "N/A"}</strong>
                    </div>
                    <div className="profile-info-row">
                      <span>Projects</span>
                      <strong>{stats.projects}</strong>
                    </div>
                    <div className="profile-info-row">
                      <span>Applications</span>
                      <strong>{stats.applications}</strong>
                    </div>
                  </div>
                </div>

                <div className="profile-card profile-danger">
                  <h2>Delete Account</h2>
                  <p>
                    This permanently deletes your profile, projects, applications,
                    messages, resumes, and login account. A deletion code will be
                    sent to your registered email before anything is removed.
                  </p>
                  <button
                    type="button"
                    className="profile-secondary-danger-btn"
                    onClick={handleSendDeleteCode}
                    disabled={sendingDeleteCode || deleting}
                  >
                    {sendingDeleteCode ? "Sending Code..." : "Send Deletion Code"}
                  </button>
                  <label className="profile-field">
                    <span>Deletion Code</span>
                    <input
                      value={deleteCode}
                      onChange={(event) => setDeleteCode(event.target.value)}
                      disabled={deleting}
                      placeholder={deleteCodeSent ? "Enter emailed code" : "Send code first"}
                    />
                  </label>
                  <button
                    type="button"
                    className="profile-danger-btn"
                    onClick={handleDeleteAccount}
                    disabled={!deleteCode.trim() || deleting}
                  >
                    {deleting ? "Deleting..." : "Delete My Account"}
                  </button>
                </div>
              </aside>
            </section>
          </>
        )}
      </div>
    </AppShell>

    {/* ── Resume Delete Advisory Modal ── */}
    {resumeToDelete && (
      <div className="resume-delete-overlay" role="dialog" aria-modal="true" aria-labelledby="resume-delete-title">
        <div className="resume-delete-modal">
          <div className="resume-delete-icon">⚠️</div>
          <h2 id="resume-delete-title">Delete Resume?</h2>
          <p className="resume-delete-name">“{resumeToDelete.name}”</p>
          <ul className="resume-delete-warnings">
            <li>The file will be <strong>permanently removed from the server</strong> and cannot be recovered.</li>
            <li>Any active application that used this resume will <strong>lose access to it</strong>, which may negatively impact your candidacy.</li>
            <li>You will need to re-upload and get admin approval for a replacement resume before applying again.</li>
          </ul>
          <div className="resume-delete-actions">
            <button
              type="button"
              className="resume-delete-cancel-btn"
              onClick={() => setResumeToDelete(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="resume-delete-confirm-btn"
              onClick={handleConfirmRemoveResume}
            >
              Yes, Delete Resume
            </button>
          </div>
        </div>
      </div>
    )}
  </>
  );
};

export default Profile;
